import type { Arquivo } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { GaleriaApi } from './galeria.api.js';
import { formatarDataHora } from '../pedidos/formatacao.js';

interface PermissoesEdicao {
  atualizar: boolean;
  excluir: boolean;
}

const MIME_IMAGEM = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * `<erp-arquivo-detalhe-modal>` — visualiza metadados reais de um
 * arquivo, edita tags/pasta/visibilidade, e exclui (soft delete real no
 * backend, `StorageRepository.excluir`). Preview de imagem usa
 * `<img src="/api/v1/storage/:id/download">` diretamente — o cookie de
 * sessão autentica a requisição da mesma forma que autenticaria
 * qualquer chamada `fetch` desta origem, sem precisar de blob/fetch
 * manual.
 */
export class ErpArquivoDetalheModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _galeriaApi: GaleriaApi | null = null;
  private _permissoes: PermissoesEdicao = { atualizar: false, excluir: false };
  private _aoSalvar: (() => void) | null = null;
  private _aoExcluir: (() => void) | null = null;

  private aberto = false;
  private arquivo: Arquivo | null = null;
  private confirmandoExclusao = false;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set galeriaApi(valor: GaleriaApi) {
    this._galeriaApi = valor;
  }

  set permissoes(valor: PermissoesEdicao) {
    this._permissoes = valor;
  }

  set aoSalvar(valor: () => void) {
    this._aoSalvar = valor;
  }

  set aoExcluir(valor: () => void) {
    this._aoExcluir = valor;
  }

  abrir(arquivo: Arquivo): void {
    this.arquivo = arquivo;
    this.confirmandoExclusao = false;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.confirmandoExclusao = false;
    this.render();
  }

  private async salvar(): Promise<void> {
    if (!this._galeriaApi || !this.arquivo || this.enviando) return;

    const folder = this.root.querySelector<HTMLInputElement>('#folder')?.value.trim() ?? '';
    const tags = this.root.querySelector<HTMLInputElement>('#tags')?.value.trim() ?? '';
    const isPublic = this.root.querySelector<HTMLInputElement>('#is-public')?.checked ?? false;
    const tagsLista = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      this.arquivo = await this._galeriaApi.atualizarMetadados(this.arquivo.id, {
        folder: folder || null,
        tags: tagsLista,
        isPublic,
      });
      this._aoSalvar?.();
      this.render();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar as alterações.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async excluir(): Promise<void> {
    if (!this._galeriaApi || !this.arquivo || this.enviando) return;

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      await this._galeriaApi.excluir(this.arquivo.id);
      this.aberto = false;
      this._aoExcluir?.();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível excluir o arquivo.';
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    if (!this.arquivo) {
      this.root.innerHTML = '';
      return;
    }
    const arquivo = this.arquivo;
    const podeEditar = this._permissoes.atualizar && !this.enviando;
    const ehImagem = MIME_IMAGEM.includes(arquivo.mimeType);
    const urlDownload = this._galeriaApi?.urlDownload(arquivo.id) ?? '';

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        .corpo { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        .preview { display: flex; align-items: center; justify-content: center; background: var(--pg-color-bg-base, #121417); border-radius: var(--pg-radius-md, 10px); min-height: 180px; overflow: hidden; }
        .preview img { max-width: 100%; max-height: 260px; display: block; }
        .preview .sem-preview { color: var(--pg-color-texto-desabilitado, #5c626b); font-size: var(--pg-font-size-sm, 0.875rem); padding: var(--pg-space-6, 32px); }
        .campo { display: flex; justify-content: space-between; gap: var(--pg-space-3, 12px); font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .campo strong { color: var(--pg-color-texto-primario, #f2f3f5); font-weight: var(--pg-font-weight-semibold, 600); }
        ${FORM_FIELD_CSS}
        .checkbox { flex-direction: row; align-items: center; gap: var(--pg-space-3, 12px); }
        .checkbox input { width: auto; }
        a.link { color: var(--pg-color-destaque, #2f8cff); font-size: var(--pg-font-size-xs, 0.75rem); }
        .aviso { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-alerta, #e0a52c); }
        .rodape { display: flex; justify-content: space-between; gap: var(--pg-space-3, 12px); }
        .rodape-acoes { display: flex; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">${escaparHtml(arquivo.originalName)}</span>
        <div class="corpo">
          <div class="preview">
            ${ehImagem ? `<img src="${urlDownload}" alt="${escaparAtributo(arquivo.originalName)}" />` : `<span class="sem-preview">.${escaparHtml(arquivo.extension)} — sem preview</span>`}
          </div>

          <div class="campo"><span>Módulo</span><strong>${escaparHtml(arquivo.module)}</strong></div>
          <div class="campo"><span>Tamanho</span><strong>${formatarTamanho(arquivo.size)}</strong></div>
          <div class="campo"><span>Tipo</span><strong>${escaparHtml(arquivo.mimeType)}</strong></div>
          <div class="campo"><span>Enviado em</span><strong>${formatarDataHora(arquivo.createdAt)}</strong></div>

          <label>Pasta <input id="folder" type="text" maxlength="128" value="${escaparAtributo(arquivo.folder ?? '')}" ${podeEditar ? '' : 'disabled'} /></label>
          <label>Tags (separadas por vírgula) <input id="tags" type="text" maxlength="200" value="${escaparAtributo(arquivo.tags.join(', '))}" ${podeEditar ? '' : 'disabled'} /></label>
          <label class="checkbox"><input id="is-public" type="checkbox" ${arquivo.isPublic ? 'checked' : ''} ${podeEditar ? '' : 'disabled'} /> Arquivo público</label>

          <a class="link" href="${urlDownload}" target="_blank" rel="noopener">Abrir/baixar arquivo original</a>

          ${this.confirmandoExclusao ? '<span class="aviso">Confirma excluir este arquivo? Esta ação não pode ser desfeita pela interface.</span>' : ''}
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </div>
        <div slot="footer" class="rodape">
          ${this._permissoes.excluir ? `<pg-button id="botao-excluir" variant="ghost">${this.confirmandoExclusao ? 'Confirmar exclusão' : 'Excluir'}</pg-button>` : '<span></span>'}
          <div class="rodape-acoes">
            <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
            ${podeEditar ? '<pg-button id="botao-salvar" variant="primary">Salvar alterações</pg-button>' : ''}
          </div>
        </div>
      </pg-modal>
    `;

    this.root.querySelector('#botao-fechar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', () => void this.salvar());
    this.root.querySelector('#botao-excluir')?.addEventListener('pg-click', () => {
      if (this.confirmandoExclusao) {
        void this.excluir();
      } else {
        this.confirmandoExclusao = true;
        this.render();
      }
    });
  }
}

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

function escaparAtributo(valor: string): string {
  return escaparHtml(valor).replace(/"/g, '&quot;');
}

customElements.define('erp-arquivo-detalhe-modal', ErpArquivoDetalheModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-arquivo-detalhe-modal': ErpArquivoDetalheModal;
  }
}
